import { gsap } from 'gsap'
import { nextTick, watch } from 'vue'

import { loaderVisible } from '@/components/organisms/SiteLoader.vue'
import { useGlobalReducedMotion } from '@/functions/useGlobalReducedMotion'
import {
  animateColorText,
  fadeIn,
  popup,
  revealCharText,
  revealText,
  scaleX,
  scaleY,
  slideUp
} from '@/utils/animations'

export enum RevealState {
  COMPLETED = 'completed',
  RUNNING = 'running',
  STALE = 'stale'
}

export type RevealAnimation = gsap.core.Tween | gsap.core.Timeline

const reducedMotion = useGlobalReducedMotion()

const ANIMATIONS = {
  animateColorText,
  fadeIn,
  popup,
  revealCharText,
  revealText,
  scaleX,
  scaleY,
  slideUp,
  none: (_, opts?: gsap.TweenVars) => gsap.to({ value: 0 }, { value: 1, duration: 0.01, ...opts })
}

const DEFAULT_ANIMATION = 'revealText'

export type RevealableElement = Element & {
  _reveal?: {
    animation?: RevealAnimation
    controlled?: boolean
    effect?: string
    group?: boolean
    groupOptions?: {
      ease?: string
      delay?: number
      duration?: number
      stagger?: number
    }
    immediate?: boolean
    observer?: IntersectionObserver
    options?: gsap.TweenVars
    startAt?: number
    parent?: RevealableElement
    destroy?: () => void
    loaderUnwatch?: () => void
    refresh?: () => void
    refreshTimeout?: number
  }
}

function getAnimation (el: Element, type?: string, options?: gsap.TweenVars): RevealAnimation {
  const effect = type in ANIMATIONS ? type : DEFAULT_ANIMATION

  el.setAttribute('data-reveal-effect', effect)
  el.setAttribute('data-reveal-state', RevealState.STALE)

  return ANIMATIONS[effect](el, {
    ...options,
    paused: true,
    onComplete: () => el.setAttribute('data-reveal-state', RevealState.COMPLETED),
    onStart: () => el.setAttribute('data-reveal-state', RevealState.RUNNING)
  })
}

function initGroup (el: RevealableElement, effect: string) {
  if (el._reveal.animation) el._reveal.animation.kill()

  el._reveal.animation = gsap.timeline({ paused: true })

  let children: (Element | RevealableElement)[] = gsap.utils.toArray('[data-reveal-child]', el)

  if (!children.length) children = Array.from(el.children)

  children.forEach((child, i) => {
    let animation: RevealAnimation
    let startAt = el._reveal.groupOptions?.stagger !== undefined
      ? i * el._reveal.groupOptions.stagger
      : undefined

    if ('_reveal' in child) {
      child._reveal.parent = el
      animation = getAnimation(child, child._reveal.effect || effect, { ...el._reveal.options, ...child._reveal.options })
      child._reveal.animation = animation
      if (child._reveal.startAt !== undefined) {
        startAt = child._reveal.startAt
      }
    } else {
      animation = getAnimation(child, effect, el._reveal.options)
    }
    // @ts-ignore
    el._reveal.animation.add(animation.play(), startAt)
  })

  el._reveal.refresh = () => {
    clearTimeout(el._reveal.refreshTimeout)
    // @ts-ignore
    el._reveal.refreshTimeout = setTimeout(() => initGroup(el, effect), 200)
  }
}

function playAnimation (el: RevealableElement) {
  const { animation, group, groupOptions: { ease, duration } = {} } = el._reveal
  if (group && (duration || ease)) {
    (animation as gsap.core.Timeline).tweenFromTo(0, animation.duration(), {
      duration,
      ease,
      onStart: () => animation.paused(false)
    })
  } else {
    animation.restart(true)
  }
}

function bindObserver (el: RevealableElement, repeat?: boolean) {
  el._reveal.observer = new IntersectionObserver(([{ isIntersecting }]) => {
    if (isIntersecting) {
      playAnimation(el)
    } else if (repeat) {
      el._reveal.animation.pause()
    } else if (!el._reveal.parent && !el._reveal.animation.paused()) { // the animation ran at least once
      el._reveal.animation.seek(el._reveal.animation.duration())
      el._reveal.destroy?.()
    }
  })

  el._reveal.observer.observe(el)
}

export default {
  created: (el: RevealableElement, { arg, value, modifiers }) => {
    if (reducedMotion.value) return
    if (!el._reveal) el._reveal = {}
    if (!arg) {
      el._reveal.effect = value
      el._reveal.group = modifiers.group
    }
    if (arg === 'options') el._reveal.options = value
    if (arg === 'group-options') el._reveal.groupOptions = value
    if (arg === 'start-at') el._reveal.startAt = value
    if (arg === 'toggle') {
      el._reveal.controlled = true
      el._reveal.immediate = value
    }
  },
  mounted: async (el: RevealableElement, { arg, modifiers, value }) => {
    if (arg || !el._reveal) return

    await nextTick()

    if (modifiers.group) {
      initGroup(el, value)
    } else if (modifiers.child) {
      el.setAttribute('data-reveal-child', '')
    } else {
      el._reveal.animation = getAnimation(el, value, el._reveal.options)
    }

    el._reveal.destroy = () => {
      clearTimeout(el._reveal.refreshTimeout)
      el._reveal.animation?.data?.destroy()
      el._reveal.animation?.kill()
      el._reveal.observer?.disconnect()
      el._reveal.parent?._reveal.refresh()
      el._reveal.loaderUnwatch?.()
    }

    if (modifiers.child) return

    const init = () => {
      if (!el._reveal.controlled) {
        bindObserver(el, modifiers.repeat)
      } else if (el._reveal.immediate) {
        playAnimation(el)
      }
    }

    if (loaderVisible.value) {
      el._reveal.loaderUnwatch = watch(loaderVisible, () => {
        el._reveal.loaderUnwatch()
        init()
      })
    } else {
      init()
    }
  },
  updated: (el: RevealableElement, { arg, value, oldValue }) => {
    // programmatically handling animation according to "toggle" arg (which is supposed to be a mutable ref)
    if (arg === 'toggle' && !el._reveal?.parent && value !== oldValue) {
      el._reveal?.animation?.[value ? 'play' : 'reverse']()
    }
  },
  beforeUnmount: (el: RevealableElement) => {
    el._reveal?.destroy?.()
  }
}
